import React, {useEffect, useMemo, useState} from 'react';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {Banner, Text, WithTooltip} from '@albert-io/atomic';
import type {ClassroomModelV1, QuestionSetModelV1, SchoolModelV5} from '@albert-io/models';
import {List, Set} from 'immutable';
import './school-translations-banner.scss';

const all = 'Students will have access to Spanish translations.' as const;

const some =
  'Students will have access to Spanish translations if their school allows translations.' as const;

type AllText = typeof all;
type SomeText = typeof some;

const isSomeText = (text: AllText | SomeText): text is SomeText => {
  return (text as SomeText) === some;
};

interface SchoolBannerProps {
  className?: string;
  classrooms: List<ClassroomModelV1>;
  questionSets: List<QuestionSetModelV1>;
}

export const SchoolTranslationsBannerWrapper = ({
  classrooms,
  questionSets,
  ...props
}: SchoolBannerProps) => {
  const [showBanner, setShowBanner] = useState(false);
  const [text, setText] = useState<AllText | SomeText>(some);

  const schoolNameList = useMemo(() => {
    const classroomsWithTranslations = List(getClassroomsWithTranslations(classrooms));
    const schoolnames = getSchools(classroomsWithTranslations).map(
      (school) => school.getName() as string
    );
    return Set(schoolnames).toArray(); // get unique names only
  }, [classrooms]);

  useEffect(() => {
    const schools = getSchools(classrooms);
    const classroomsWithTranslations = getClassroomsWithTranslations(classrooms);
    const assignmentHasTranslatedQuestions = checkAssignmentHasTranslations(questionSets);

    if (classroomsWithTranslations.length > 0 && assignmentHasTranslatedQuestions) {
      setText(classroomsWithTranslations.length === schools.length ? all : some);
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [classrooms, questionSets]);

  return (
    <FeatureFlag name='assignment_question_translations'>
      {showBanner && (
        <Banner border={false} cardClassName='translations-banner__card' {...props}>
          <Banner.Icon className='large-icon' icon={['fal', 'language']} />
          <Text size='s'>{text}</Text>
          {isSomeText(text) && (
            <WithTooltip
              content={
                <>
                  Schools with translations allowed:
                  <ul>
                    {schoolNameList.map((name) => (
                      <li>{name}</li>
                    ))}
                  </ul>
                </>
              }
            >
              <Banner.Icon className='large-icon' icon={['fal', 'question-circle']} />
            </WithTooltip>
          )}
        </Banner>
      )}
    </FeatureFlag>
  );
};

const getSchools = (classrooms: List<ClassroomModelV1>) =>
  classrooms.toArray().map((classroom) => classroom.getSchool() as SchoolModelV5);

const getClassroomsWithTranslations = (classrooms: List<ClassroomModelV1>) =>
  classrooms.toArray().filter((classroom) => {
    const school = classroom.getSchool();
    return school.isAllowTranslations();
  });

const checkAssignmentHasTranslations = (questionSets: List<QuestionSetModelV1>) =>
  questionSets.toArray().some((questionSet) => {
    const subject = questionSet.getSubject();
    return subject.isTranslateQuestions() || subject.isTranslateSupplements();
  });
